import { matchesLoyaltyDoubleRewardsOfferCriteria } from 'takeovers/loyaltyChooseDoubleRewardsTakeover/utils/loyaltyChooseDoubleRewardsMetadata'
import { SOCIETE_CHOOSE_DOUBLE_REWARDS_TAKEOVER_ID } from 'takeovers/loyaltyChooseDoubleRewardsTakeover/loyaltyChooseDoubleRewardsTakeover.constants'
import { LabFeatureFlags } from 'constants/lab'
import { useAppSelector } from 'hooks/reduxHooks'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import { isLabSiteTakeoverPayload, isTakeoverId } from 'lib/lab/dynamicPayload'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { takeovers } from 'takeovers/takeoverRegistry'

// Union type for all supported offer types in takeovers
type SupportedOfferTypes =
  | App.Offer
  | App.OfferSummary
  | App.HotelOffer
  | App.HotelOfferSummary
  | App.BedbankOffer
  | App.BedbankOfferSummary
  | App.Tours.TourV2Offer
  | App.Tours.TourV2OfferSummary
  | App.CruiseOffer
  | App.VillaOffer
  | App.VillaOfferSummary
  | App.BedbankOffer
  | App.TourOffer
  | App.TourOfferSummary
  | App.ExperienceOffer

export interface TakeoverOptions {
  /**
   * Check which takeover applies to the vertical given
   */
  vertical?: App.SiteTakeoverVertical
  /**
   * Check which takeover applies to the offer given
   */
  offer?: SupportedOfferTypes
  /**
   * Check which takeover applies for specifically the hero carousel condition
   */
  heroVertical?: App.SiteTakeoverHeroVertical
}

/**
 * Type guard to check if an offer has the 'type' property needed for offer type filtering.
 * Validates that the offer contains the 'type' property required for matching against takeover offer types.
 *
 * @param offer - The offer object to validate, can be any of the supported offer types
 * @returns True if the offer has the type property, false otherwise
 */
function isOfferWithType(offer: SupportedOfferTypes): offer is SupportedOfferTypes & { type: App.OfferType } {
  return 'type' in offer && typeof offer.type === 'string'
}

/**
 * Type guard to check if an offer has the 'campaignTags' property needed for campaign tag filtering.
 * Validates that the offer contains the 'campaignTags' property required for matching against takeover campaign tags.
 *
 * @param offer - The offer object to validate, can be any of the supported offer types
 * @returns True if the offer has the campaignTags property as an array, false otherwise
 */
function isOfferWithCampaignTags(offer: SupportedOfferTypes): offer is SupportedOfferTypes & { campaignTags: Array<string> } {
  return 'campaignTags' in offer && Array.isArray(offer.campaignTags)
}

/**
 * @note - Removable post choose your double rewards campaign
 */
export function isOfferEligibleForDoubleRewardsTakeover(
  offer: SupportedOfferTypes,
  metadataFlag: App.LabFeatureFlagResult | undefined,
): boolean {
  return matchesLoyaltyDoubleRewardsOfferCriteria({
    type: isOfferWithType(offer) ? offer.type : undefined,
    campaignTags: isOfferWithCampaignTags(offer) ? offer.campaignTags : undefined,
  }, metadataFlag)
}

/**
 * Determines if a takeover is applicable based on the provided filtering options.
 * Checks vertical targeting, hero vertical targeting, offer type matching, and campaign tag matching.
 * All conditions must be satisfied for the takeover to be considered applicable.
 *
 * @param takeover - The site takeover configuration to check applicability for
 * @param options - Filtering options to determine applicability
 * @param options.vertical - Optional vertical filter (e.g., 'flights', 'hotels')
 * @param options.offer - Optional offer to match against takeover's offer type and campaign tags
 * @param options.heroVertical - Optional hero vertical filter for hero carousel sections
 * @returns True if the takeover matches all provided filtering criteria, false otherwise
 *
 * @example
 * ```tsx
 * // Check if takeover applies to flights vertical
 * const isApplicable = isTakeoverApplicable(takeover, { vertical: 'flights' })
 *
 * // Check if takeover applies to a specific offer
 * const isOfferApplicable = isTakeoverApplicable(takeover, { offer: myOffer })
 *
 * // Check multiple criteria
 * const isFullyApplicable = isTakeoverApplicable(takeover, {
 *   vertical: 'hotels',
 *   heroVertical: 'hotels',
 *   offer: hotelOffer
 * })
 * ```
 */
export function isTakeoverApplicable(
  takeover: App.SiteTakeoverConfig,
  options: TakeoverOptions,
): boolean {
  const { vertical, offer, heroVertical } = options

  // Is the takeover for this particular vertical?
  const isVerticalApplicable = vertical ? takeover.verticals.has(vertical) : true

  // Is the takeover for this particular hero vertical? Fallback to upper level vertical if not specified
  const isHeroVerticalApplicable = heroVertical ? (takeover.heroSection?.verticals?.has(heroVertical) ?? true) : true

  // Is the takeover for this particular offer type?
  let isOfferTypeApplicable = true
  if (offer && takeover.offerTypes) {
    isOfferTypeApplicable = isOfferWithType(offer) && takeover.offerTypes.has(offer.type)
  }

  // Is the takeover for this particular campaign tag?
  let isCampaignTagApplicable = true
  if (offer && takeover.campaignTags) {
    isCampaignTagApplicable =
      isOfferWithCampaignTags(offer) &&
      offer.campaignTags.some(tag => takeover.campaignTags?.has(tag))
  }

  return isVerticalApplicable &&
    isHeroVerticalApplicable &&
    isOfferTypeApplicable &&
    isCampaignTagApplicable
}

/**
 * Hook-free variant for code paths that already have App.State, keeping takeover resolution testable and reusable.
 * This version is for use in SSR.
 */
export function getCurrentSiteTakeover(state: App.State, options: TakeoverOptions = {}): App.SiteTakeoverConfig | undefined {
  const enableSiteTakeover = state.system.features.labFlags[LabFeatureFlags.enableSiteTakeover]
  const regionCode = state.geo.currentRegionCode
  const isLoggedIn = selectLoggedIn(state)
  const isCurrentRegionUserLoyaltyRegion = getIsCurrentRegionUserLoyaltyRegion(state)
  if (!enableSiteTakeover?.enabled || !isLabSiteTakeoverPayload(enableSiteTakeover.value)) {
    return undefined
  }

  const takeoverId = enableSiteTakeover.value.id
  if (!isTakeoverId(takeoverId)) {
    return undefined
  }

  const takeover = takeovers[takeoverId]
  if (!takeover) {
    return undefined
  }

  if (takeover.regions && !takeover.regions.has(regionCode)) {
    return undefined
  }

  if (takeover.hideOnLoyaltyRegionMismatch && isLoggedIn && !isCurrentRegionUserLoyaltyRegion) {
    return undefined
  }

  // Temporary eligibility stub: all hotel, cruise and tour offers are included; campaign tagging all those offers isn't viable. Check date on labFlags on whether still active
  if (options.offer && takeover.id === SOCIETE_CHOOSE_DOUBLE_REWARDS_TAKEOVER_ID) {
    const offer = options.offer
    const metadataFlag = state.system.features.labFlags[LabFeatureFlags.enableSiteTakeoverMetadata]

    const eligible = isOfferEligibleForDoubleRewardsTakeover(offer, metadataFlag)
    if (!eligible) return undefined
  }

  return isTakeoverApplicable(takeover, options) ? takeover : undefined
}

/**
 * Hook to get the current active site takeover configuration (V2 implementation).
 * This version uses lab feature flags to determine which takeover to display,
 * allowing for controlled rollouts and A/B testing of takeovers.
 *
 * @param options - Configuration options for filtering takeovers by vertical, offer, or hero vertical
 * @param options.vertical - Filter takeovers by specific vertical (e.g., 'flights', 'hotels')
 * @param options.offer - Filter takeovers by specific offer properties (type, campaign tags)
 * @param options.heroVertical - Filter takeovers for hero carousel sections by vertical
 * @returns The active takeover configuration if applicable, undefined otherwise
 *
 * @example
 * ```tsx
 * // Get any active takeover
 * const takeover = useCurrentSiteTakeover()
 *
 * // Get takeover specific to flights vertical
 * const flightsTakeover = useCurrentSiteTakeover({ vertical: 'flights' })
 *
 * // Get takeover for a specific offer
 * const offerTakeover = useCurrentSiteTakeover({ offer: myOffer })
 * ```
 */
export function useCurrentSiteTakeover(options: TakeoverOptions = {}): App.SiteTakeoverConfig | undefined {
  return useAppSelector(state => getCurrentSiteTakeover(state, options))
}
